import { useTranslation } from 'react-i18next';
import { AlertCircle, ChevronDown, FileCheck, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConsentEventRow } from './ConsentEventRow';
import { JournalNewerEventsNotice } from './JournalNewerEventsNotice';
import { JournalRefreshFailedNotice } from './JournalRefreshFailedNotice';
import { useConsentJournal } from './useConsentJournal';

/**
 * Acceptance of the Terms and the Privacy Policy by the account's users, and
 * their marketing-mail choices (#1100).
 *
 * These rows sat in the participant-consent journal, under a heading about call
 * participants and next to «Withdraw». They are evidence of the account's
 * agreement, not a participant's consent, so they live in a block of their own
 * with no action: the Terms cannot be «withdrawn» from here, and marketing mail
 * is switched off where it is switched on — under Notifications.
 *
 * A failure here is reported in place: the participant journal above does not
 * depend on it and stays usable.
 */
export function AccountAgreementsJournal() {
  const { t } = useTranslation(['settings', 'common']);
  const journal = useConsentJournal('account');

  return (
    <div data-testid="account-agreements-journal">
      <h3 className="text-sm font-medium text-fg">
        {t('consent.accountAgreementsTitle')}
      </h3>
      <p className="mt-1 mb-3 text-xs text-fg-muted">
        {t('consent.accountAgreementsNotice')}
      </p>

      {journal.isPending ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-6 w-6 animate-spin text-fg-muted" />
        </div>
      ) : journal.loadFailed ? (
        <div className="flex items-start gap-2 p-3 rounded-card border border-border bg-bg-surface">
          <AlertCircle className="h-4 w-4 shrink-0 text-warning" />
          <p className="text-xs text-fg-muted">
            {t('common:common.errorLoadingData', 'Failed to load data')}
          </p>
        </div>
      ) : journal.events.length === 0 ? (
        <div className="flex flex-col items-center py-6 text-center">
          <FileCheck className="h-8 w-8 text-fg-muted mb-2" />
          <p className="text-sm text-fg-muted">{t('consent.noAccountAgreements')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {journal.newerAvailable && (
            <JournalNewerEventsNotice onRefresh={journal.refresh} />
          )}
          {journal.events.map((event) => (
            <ConsentEventRow key={event.id} event={event} />
          ))}
          {journal.loadMoreFailed && (
            <p className="text-xs text-danger" role="alert">
              {t('common:common.errorLoadingData', 'Failed to load data')}
            </p>
          )}
          {journal.hasMore && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={journal.loadMore}
                disabled={journal.loadMoreBlocked}
              >
                <ChevronDown className="h-4 w-4 mr-1" />
                {t('common:common.loadMore', 'Load More')}
              </Button>
            </div>
          )}
        </div>
      )}
      {/* Outside the branches: «nothing recorded» is a claim too, and an
          empty journal that could not be re-read must not keep making it. */}
      {journal.refreshFailed && <JournalRefreshFailedNotice onRetry={journal.refresh} />}
    </div>
  );
}
